// Spec ref: docs/superpowers/specs/2026-05-21-backend-extension-ui-wireup.md
// Partecipazioni list — wired su /participations con quick tabs status,
// rich table, paginazione. Stesso pattern di sopralluoghi list.

import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useMatchRoute,
  useNavigate,
} from '@tanstack/react-router'
import { hasTokens } from '../lib/auth'
import { calendarDaysUntil } from '../lib/dateRelative'
import { useParticipations } from '../hooks/useParticipations'
import { useMe } from '../hooks/useAuth'
import { CardSkeleton } from '../components/Skeleton'
import { EmptyState } from '../components/EmptyState'
import type { ParticipationRow } from '../lib/api'

type PartecipazioniSearch = {
  page: number
  page_size: number
  q: string
  status?: string
  tipo_asta?: string
  client_id?: string
  sort: string
}

export const Route = createFileRoute('/partecipazioni')({
  beforeLoad: () => {
    if (!hasTokens()) {
      throw redirect({ to: '/login' })
    }
  },
  validateSearch: (search: Record<string, unknown>): PartecipazioniSearch => {
    const toNum = (v: unknown, dflt: number): number => {
      const n = typeof v === 'number' ? v : Number(v)
      return Number.isFinite(n) && n > 0 ? n : dflt
    }
    const toStr = (v: unknown): string => (typeof v === 'string' ? v : '')
    const toOptStr = (v: unknown): string | undefined =>
      typeof v === 'string' && v.length > 0 ? v : undefined
    return {
      page: toNum(search.page, 1),
      page_size: toNum(search.page_size, 50),
      q: toStr(search.q),
      status: toOptStr(search.status),
      tipo_asta: toOptStr(search.tipo_asta),
      client_id: toOptStr(search.client_id),
      sort: toStr(search.sort) || '-created_at',
    }
  },
  component: PartecipazioniPage,
})

const STATUS_LABEL: Record<string, string> = {
  draft: 'Bozza',
  checklist_open: 'Checklist',
  domanda_fatta: 'Domanda fatta',
  asta_in_corso: 'Asta in corso',
  aggiudicata: 'Aggiudicata',
  non_aggiudicata: 'Non aggiudicata',
  ritirata: 'Ritirata',
  contestata: 'Contestata',
  archivio: 'Archivio',
}

const STATUS_VARIANT: Record<
  string,
  'ok' | 'accent' | 'warn' | 'risk' | 'outline'
> = {
  draft: 'outline',
  checklist_open: 'warn',
  domanda_fatta: 'accent',
  asta_in_corso: 'accent',
  aggiudicata: 'ok',
  non_aggiudicata: 'risk',
  ritirata: 'risk',
  contestata: 'warn',
  archivio: 'outline',
}

const STATUS_DOT: Record<string, string> = {
  ok: 'var(--ok)',
  accent: 'var(--accent)',
  warn: 'var(--warn)',
  risk: 'var(--risk)',
  outline: 'var(--ink-3)',
}

const TIPO_ASTA_LABEL: Record<string, string> = {
  telematica: 'Telematica',
  fisica: 'Fisica',
  mista: 'Mista',
  sconosciuta: '—',
}

// Quick tabs — backend status accetta comma-separated -> IN clause.
const QUICK_TABS: Array<{
  key: string
  label: string
  filter: { status?: string; sort?: string }
}> = [
  { key: 'tutti', label: 'Tutti', filter: {} },
  {
    key: 'attive',
    label: 'Attive',
    filter: {
      status: 'draft,checklist_open,domanda_fatta,asta_in_corso',
    },
  },
  {
    key: 'checklist',
    label: 'Checklist',
    filter: { status: 'checklist_open' },
  },
  {
    key: 'domanda',
    label: 'Domanda fatta',
    filter: { status: 'domanda_fatta' },
  },
  {
    key: 'aggiudicate',
    label: 'Aggiudicate',
    filter: { status: 'aggiudicata' },
  },
  {
    // Per Giulia: aste vinte, iter post-aggiudicazione (verbale -> riparto).
    // Stesso filtro di "Aggiudicate" ma ordinato oldest-first (urgenti
    // da chiudere). Click row -> detail con sezione Iter post-agg attiva.
    key: 'iter-post-agg',
    label: 'Iter post-agg',
    filter: { status: 'aggiudicata', sort: 'data_aggiudicazione' },
  },
  {
    key: 'non-aggiudicate',
    label: 'Non aggiudicate',
    filter: { status: 'non_aggiudicata,ritirata' },
  },
  {
    key: 'archivio',
    label: 'Archivio',
    filter: { status: 'archivio' },
  },
]

function PartecipazioniPage() {
  const matchRoute = useMatchRoute()
  const onChild = matchRoute({ to: '/partecipazioni/$id', fuzzy: true })
  if (onChild) return <Outlet />
  return <PartecipazioniList />
}

function PartecipazioniList() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { data: me } = useMe()

  const params = {
    page: search.page,
    page_size: search.page_size,
    q: search.q || undefined,
    status: search.status,
    tipo_asta: search.tipo_asta,
    client_id: search.client_id,
    sort: search.sort,
  }
  const { data, isLoading, isError } = useParticipations(params)
  const items = data?.items ?? []
  const total = data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / search.page_size))

  // KPI dal subset visibile.
  const aggiudicate = items.filter((p) => p.status === 'aggiudicata').length
  const attive = items.filter((p) =>
    ['draft', 'checklist_open', 'domanda_fatta', 'asta_in_corso'].includes(
      p.status,
    ),
  ).length
  // Match tab by status + sort (per distinguere "Aggiudicate" da "Iter post-agg"
  // che hanno stesso status ma sort diverso).
  const activeTab =
    QUICK_TABS.find((t) => {
      const sameStatus =
        (t.filter.status ?? undefined) === (search.status ?? undefined)
      const sameSort =
        (t.filter.sort ?? '-created_at') === (search.sort ?? '-created_at')
      return sameStatus && sameSort
    })?.key ?? 'tutti'

  function setTab(tab: (typeof QUICK_TABS)[number]) {
    void navigate({
      search: (old: PartecipazioniSearch) => ({
        ...old,
        page: 1,
        status: tab.filter.status,
        sort: tab.filter.sort ?? '-created_at',
      }),
    })
  }

  function setSearchQ(q: string) {
    void navigate({
      search: (old: PartecipazioniSearch) => ({ ...old, page: 1, q }),
    })
  }

  function setPage(p: number) {
    void navigate({
      search: (old: PartecipazioniSearch) => ({ ...old, page: p }),
    })
  }

  // me available per future write-action gating.
  void me

  return (
    <>
      <div
        className="page-title-row"
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 'var(--sp-3)',
          marginBottom: 'var(--sp-3)',
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 'var(--fs-20)',
            fontWeight: 'var(--fw-semibold)',
          }}
        >
          Partecipazioni
        </h1>
        <span className="muted total" style={{ fontSize: 'var(--fs-13)' }}>
          <b>{total.toLocaleString('it-IT')}</b> totali
          {attive > 0 && (
            <>
              {' · '}
              <b>{attive}</b> attive
            </>
          )}
          {aggiudicate > 0 && (
            <>
              {' · '}
              <b>{aggiudicate}</b> aggiudicate
            </>
          )}
        </span>
      </div>

      {/* Quick tabs */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--sp-2)',
          borderBottom: '1px solid var(--line)',
          marginBottom: 'var(--sp-3)',
          fontSize: 'var(--fs-12)',
          overflowX: 'auto',
        }}
      >
        {QUICK_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t)}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px 6px',
              cursor: 'pointer',
              color: activeTab === t.key ? 'var(--ink-1)' : 'var(--ink-3)',
              fontWeight:
                activeTab === t.key
                  ? 'var(--fw-semibold)'
                  : 'var(--fw-regular)',
              borderBottom:
                activeTab === t.key
                  ? '2px solid var(--accent)'
                  : '2px solid transparent',
              whiteSpace: 'nowrap',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--sp-2)',
          alignItems: 'center',
          marginBottom: 'var(--sp-2)',
        }}
      >
        <input
          type="search"
          placeholder="Cerca per cliente, lotto, indirizzo, contestazione…"
          value={search.q}
          onChange={(e) => setSearchQ(e.target.value)}
          style={{
            flex: '0 1 360px',
            padding: '6px 12px',
            fontSize: 'var(--fs-12)',
            borderRadius: 'var(--r-md)',
            border: '1px solid var(--line)',
            background: 'var(--surface)',
          }}
        />
        <span className="muted" style={{ fontSize: 'var(--fs-11)' }}>
          {total.toLocaleString('it-IT')} {search.q ? 'risultati' : 'totali'}
        </span>
      </div>

      {/* Table */}
      {isLoading && <CardSkeleton />}
      {isError && (
        <div className="error" style={{ padding: 'var(--sp-4)' }}>
          Errore caricamento partecipazioni.
        </div>
      )}
      {!isLoading && !isError && items.length === 0 && (
        <EmptyState
          title="Nessuna partecipazione"
          body={
            search.q || search.status
              ? 'Nessun match per filtri attivi. Reset filtri o cambia tab.'
              : 'Non ci sono partecipazioni ancora. Crea una nuova da lotto o cliente match.'
          }
        />
      )}
      {!isLoading && items.length > 0 && (
        <div
          style={{
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-md)',
            background: 'var(--surface)',
            overflow: 'hidden',
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--fs-12)',
            }}
          >
            <thead>
              <tr style={{ background: 'var(--bg-2)' }}>
                <Th>Codice</Th>
                <Th>Cliente</Th>
                <Th>Lotto</Th>
                <Th>Data asta</Th>
                <Th>Tipo</Th>
                <Th>Offerta</Th>
                <Th>Stato</Th>
                <Th>Checklist</Th>
                <Th>Consulente</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <PartecipazioneRow key={p.id} p={p} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && pages > 1 && (
        <div
          style={{
            display: 'flex',
            gap: 'var(--sp-2)',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: 'var(--sp-3)',
            fontSize: 'var(--fs-12)',
          }}
        >
          <span className="muted">
            Pagina <b>{search.page}</b> di <b>{pages}</b>
          </span>
          <button
            type="button"
            className="btn sm"
            disabled={search.page <= 1}
            onClick={() => setPage(search.page - 1)}
          >
            ‹ Prev
          </button>
          <button
            type="button"
            className="btn sm"
            disabled={search.page >= pages}
            onClick={() => setPage(search.page + 1)}
          >
            Next ›
          </button>
        </div>
      )}
    </>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '8px 10px',
        fontSize: 'var(--fs-10)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--tracking-caps)',
        fontWeight: 'var(--fw-semibold)',
        color: 'var(--ink-3)',
        borderBottom: '1px solid var(--line)',
      }}
    >
      {children}
    </th>
  )
}

function PartecipazioneRow({ p }: { p: ParticipationRow }) {
  const pCode = `PA_${String(p.short_code).padStart(5, '0')}`
  const cCode = `C_${String(p.client_short_code).padStart(5, '0')}`
  const lCode = `L_${String(p.property_short_code).padStart(5, '0')}`
  const status = p.status || ''
  const variant = STATUS_VARIANT[status] ?? 'outline'
  const statusLabel = STATUS_LABEL[status] ?? status.replace(/_/g, ' ')

  const dataAsta = p.property_data_asta
  const dtStr = dataAsta
    ? new Date(dataAsta).toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null
  const relTime = dataAsta ? relativeFromNow(new Date(dataAsta)) : null

  const checklistPct =
    p.checklist_total > 0
      ? Math.round((p.checklist_done / p.checklist_total) * 100)
      : null

  const cIni = p.client_name
    ? p.client_name
        .split(/\s+/)
        .map((s) => s[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '?'

  return (
    <tr style={{ borderBottom: '1px solid var(--line)' }}>
      <td style={cellStyle}>
        <Link
          to="/partecipazioni/$id"
          params={{ id: p.id }}
          search={{} as never}
          className="tag code"
          style={{
            fontSize: 'var(--fs-11)',
            fontFamily: 'var(--font-mono)',
            color: 'var(--accent-ink)',
          }}
        >
          {pCode}
        </Link>
      </td>
      <td style={cellStyle}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-1)' }}
        >
          <div
            className="avatar sm"
            style={{ background: avatarColor(p.client_id) }}
          >
            {cIni}
          </div>
          <div style={{ minWidth: 0 }}>
            <Link
              to="/clienti/$id"
              params={{ id: p.client_id }}
              search={{} as never}
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 140,
                display: 'block',
                color: 'var(--ink-1)',
              }}
            >
              {p.client_name ?? '—'}
            </Link>
            <div
              className="muted"
              style={{
                fontSize: 'var(--fs-10)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              {cCode}
            </div>
          </div>
        </div>
      </td>
      <td style={cellStyle}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          <Link
            to="/lotti/$id"
            params={{ id: p.property_id }}
            search={{} as never}
            style={{
              color: 'var(--ink-1)',
              fontWeight: 'var(--fw-medium)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 180,
            }}
          >
            {p.property_address ?? '—'}
          </Link>
          <span
            className="muted"
            style={{
              fontSize: 'var(--fs-10)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {lCode}
            {p.property_comune && ` · ${p.property_comune}`}
          </span>
        </div>
      </td>
      <td style={cellStyle}>
        {dtStr ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span style={{ fontFamily: 'var(--font-mono)' }}>{dtStr}</span>
            {relTime && (
              <span
                className="muted"
                style={{
                  fontSize: 'var(--fs-10)',
                  color:
                    relTime.kind === 'today'
                      ? 'var(--accent-ink)'
                      : relTime.kind === 'past'
                        ? 'var(--ink-3)'
                        : 'var(--ink-2)',
                }}
              >
                {relTime.label}
              </span>
            )}
          </div>
        ) : (
          <span
            className="chip warn"
            title="Data asta non ancora definita"
            style={{
              fontSize: 'var(--fs-10)',
              padding: '2px 8px',
              background: 'oklch(96% 0.05 80)',
              color: 'oklch(45% 0.15 60)',
              border: '1px solid oklch(85% 0.08 80)',
              borderRadius: 'var(--r-pill)',
              fontWeight: 'var(--fw-semibold)',
            }}
          >
            ⚠ da definire
          </span>
        )}
      </td>
      <td style={cellStyle}>
        <span
          className="chip outline"
          style={{ fontSize: 'var(--fs-10)', padding: '2px 8px' }}
        >
          {TIPO_ASTA_LABEL[p.tipo_asta] ?? p.tipo_asta}
        </span>
      </td>
      <td style={cellStyle}>
        {p.status === 'aggiudicata' && p.prezzo_aggiudicazione != null ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontWeight: 'var(--fw-semibold)',
                color: 'var(--ok-ink, #15803d)',
              }}
            >
              {fmtMoney(p.prezzo_aggiudicazione)}
            </span>
            <span
              className="muted"
              style={{ fontSize: 'var(--fs-10)' }}
            >
              aggiud.
            </span>
          </div>
        ) : p.offerta_prezzo != null ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span style={{ fontFamily: 'var(--font-mono)' }}>
              {fmtMoney(p.offerta_prezzo)}
            </span>
            {p.rilancio_prev != null && (
              <span
                className="muted"
                style={{
                  fontSize: 'var(--fs-10)',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                +{fmtMoney(p.rilancio_prev)} ril.
              </span>
            )}
          </div>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td style={cellStyle}>
        <span
          className={`chip ${variant}`}
          style={{ fontSize: 'var(--fs-10)' }}
        >
          <span
            className="dot"
            style={{ background: STATUS_DOT[variant] }}
          />
          {statusLabel}
        </span>
      </td>
      <td style={cellStyle}>
        {p.checklist_total > 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              minWidth: 60,
            }}
          >
            <span
              style={{
                fontSize: 'var(--fs-10)',
                fontFamily: 'var(--font-mono)',
                color:
                  checklistPct === 100
                    ? 'var(--ok-ink, #15803d)'
                    : 'var(--ink-2)',
              }}
            >
              {p.checklist_done}/{p.checklist_total}
            </span>
            <div
              style={{
                height: 4,
                background: 'var(--bg-2)',
                borderRadius: 999,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${checklistPct ?? 0}%`,
                  background:
                    checklistPct === 100
                      ? 'var(--ok, #16a34a)'
                      : 'var(--accent, #f97316)',
                  transition: 'width 200ms',
                }}
              />
            </div>
          </div>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td style={cellStyle}>
        {p.consultant_name ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div
              className="avatar xs"
              style={{
                background: avatarColor(p.consultant_id),
                width: 18,
                height: 18,
                fontSize: 9,
              }}
            >
              {p.consultant_name
                .split(/\s+/)
                .map((s) => s[0])
                .slice(0, 2)
                .join('')
                .toUpperCase()}
            </div>
            <span style={{ fontSize: 'var(--fs-11)' }}>
              {p.consultant_name.split(' ')[0]}
            </span>
          </div>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td style={{ ...cellStyle, textAlign: 'right' }}>
        <Link
          to="/partecipazioni/$id"
          params={{ id: p.id }}
          search={{} as never}
          className="btn sm ghost"
          style={{ padding: '2px 8px' }}
        >
          →
        </Link>
      </td>
    </tr>
  )
}

const cellStyle: React.CSSProperties = {
  padding: '8px 10px',
  verticalAlign: 'middle',
}

function fmtMoney(v: number | null | undefined): string {
  if (v == null) return '—'
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(v)
}

function relativeFromNow(d: Date): {
  kind: 'today' | 'soon' | 'past' | 'future'
  label: string
} {
  const calDays = calendarDaysUntil(d)
  if (calDays < -1) return { kind: 'past', label: `${-calDays}g fa` }
  if (calDays === -1) return { kind: 'past', label: 'ieri' }
  if (calDays === 0) return { kind: 'today', label: 'oggi' }
  if (calDays === 1) return { kind: 'today', label: 'domani' }
  if (calDays <= 7) return { kind: 'soon', label: `${calDays} gg` }
  return { kind: 'future', label: d.toLocaleDateString('it-IT') }
}

function avatarColor(seed: string): string {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0
  const hue = Math.abs(h) % 360
  return `oklch(58% 0.14 ${hue})`
}
